import type { ApiConfig, ApiResponse } from '../types/api';

/**
 * Optional async token source. When set via `setTokenProvider`, the client
 * re-asks for a fresh bearer on every request — required for MF mode where
 * the host (scuba) rotates tokens under us.
 */
export type TokenProvider = () => Promise<string | null>;

/**
 * Base API client with common functionality
 */
export class BaseApiClient {
  protected config: Required<ApiConfig>;
  protected tokenProvider: TokenProvider | null = null;

  private static readonly HTTP_REQUEST_TIMEOUT = 408;
  private static readonly HTTP_UNKNOWN_ERROR = 0;
  private static readonly AUTH_HEADER_PREVIEW_LENGTH = 20;
  // Upper bound for the per-request `tokenProvider()` call. The provider is
  // expected to return a cached bearer synchronously-ish (no network hit), so
  // anything slower than this is treated as a hang. Without this bound a
  // provider that never resolves (e.g. a wedged host scuba bridge in MF mode)
  // would stall the request forever — it runs before the fetch AbortController
  // arms — permanently sticking any UI gated on the request's pending state.
  private static readonly TOKEN_PROVIDER_TIMEOUT = 5000;

  constructor(config: ApiConfig) {
    this.config = {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      ...config,
    };
  }

  /**
   * Make an HTTP request
   */
  protected async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${this.config.baseUrl}${endpoint}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    // Debug: Log the headers being sent
    const callerHeaders = (options.headers as Record<string, string>) || {};
    const headers: Record<string, string> = {
      ...this.config.headers,
      ...callerHeaders,
    };

    // If a tokenProvider is registered (MF/hosted mode), re-fetch the bearer
    // for each request so a host-side refresh doesn't strand us with a stale
    // token. The provider returns a cached scuba string — no network hit.
    // Caller-supplied `options.headers.Authorization` always wins; the cached
    // config header must NOT — otherwise setAuthToken()/refreshAuthToken()
    // would permanently short-circuit the provider.
    //
    // The fetch below is guarded by the AbortController; this token fetch
    // happens before it, so it is bounded by its own timeout
    // (`getFreshTokenWithTimeout`) to keep a wedged provider from hanging the
    // request indefinitely.
    if (this.tokenProvider && !callerHeaders.Authorization) {
      try {
        const freshToken = await this.getFreshTokenWithTimeout();
        if (freshToken) {
          headers.Authorization = `Bearer ${freshToken}`;
        } else {
          delete headers.Authorization;
        }
      } catch (err) {
        console.warn(
          '[ApiClient] tokenProvider failed or timed out; proceeding without fresh token',
          err,
        );
      }
    }

    console.log('[ApiClient] Request:', {
      url,
      method: options.method || 'GET',
      hasAuthHeader: !!headers.Authorization,
      authHeaderPreview: headers.Authorization
        ? `${headers.Authorization.substring(0, BaseApiClient.AUTH_HEADER_PREVIEW_LENGTH)}...`
        : 'MISSING',
    });

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

        const contentType = response.headers.get('content-type');
        if (contentType?.includes('text/plain')) {
          try {
            const textBody = await response.text();
            errorMessage = textBody || errorMessage;
          } catch {
            // If parsing fails, use default message
          }
        }

        throw new ApiError(errorMessage, response.status);
      }

      // Handle different content types
      const contentType = response.headers.get('content-type');
      let data: T;

      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else if (contentType?.includes('text/')) {
        data = (await response.text()) as unknown as T;
      } else {
        data = (await response.blob()) as unknown as T;
      }

      return {
        data,
        status: response.status,
        statusText: response.statusText,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timeout', BaseApiClient.HTTP_REQUEST_TIMEOUT);
        }
        throw new ApiError(error.message, BaseApiClient.HTTP_UNKNOWN_ERROR);
      }

      throw new ApiError('Unknown error occurred', BaseApiClient.HTTP_UNKNOWN_ERROR);
    }
  }

  /**
   * Builds the final endpoint string for a GET request, including query parameters.
   * @protected
   */
  protected _buildGetEndpoint(
    endpoint: string,
    params?: Record<string, string | number | boolean>,
  ): string {
    if (!params) {
      return endpoint;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${endpoint}?${queryString}` : endpoint;
  }

  /**
   * GET request
   */
  protected async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean>,
  ): Promise<ApiResponse<T>> {
    const finalEndpoint = this._buildGetEndpoint(endpoint, params);

    return this.request<T>(finalEndpoint, {
      method: 'GET',
    });
  }

  /**
   * GET request without authorization header (for public endpoints)
   */
  protected async getWithoutAuth<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean>,
  ): Promise<ApiResponse<T>> {
    const finalEndpoint = this._buildGetEndpoint(endpoint, params);
    const url = `${this.config.baseUrl}${finalEndpoint}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    // Debug: Log that this is a request without auth
    console.log('[ApiClient] Request (no auth):', {
      url,
      method: 'GET',
      hasAuthHeader: false,
    });

    try {
      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        // No headers - don't include Authorization
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const body = await response.text();
          if (body) {
            errorMessage = body;
          }
        } catch {
          // fall back to default message
        }
        throw new ApiError(errorMessage, response.status);
      }

      let data: T;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else if (contentType?.includes('text/')) {
        data = (await response.text()) as unknown as T;
      } else {
        data = (await response.blob()) as unknown as T;
      }

      return {
        data,
        status: response.status,
        statusText: response.statusText,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timeout', BaseApiClient.HTTP_REQUEST_TIMEOUT);
        }
        throw new ApiError(error.message, BaseApiClient.HTTP_UNKNOWN_ERROR);
      }

      throw new ApiError('Unknown error occurred', BaseApiClient.HTTP_UNKNOWN_ERROR);
    }
  }

  /**
   * POST request
   */
  protected async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    const options: RequestInit = {
      method: 'POST',
    };

    if (body !== undefined) {
      if (typeof body === 'string') {
        options.body = body;
        options.headers = {
          'Content-Type': 'text/plain',
        };
      } else {
        options.body = JSON.stringify(body);
        options.headers = {
          'Content-Type': 'application/json',
        };
      }
    }

    return this.request<T>(endpoint, options);
  }

  /**
   * POST request without authorization header (for APIs that handle auth differently)
   */
  protected async postWithoutAuth<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    const url = `${this.config.baseUrl}${endpoint}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    const options: RequestInit = {
      method: 'POST',
      signal: controller.signal,
    };

    if (body !== undefined) {
      if (typeof body === 'string') {
        options.body = body;
        options.headers = {
          'Content-Type': 'text/plain',
        };
      } else {
        options.body = JSON.stringify(body);
        options.headers = {
          'Content-Type': 'application/json',
        };
      }
    }

    try {
      const response = await fetch(url, options);
      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const body = await response.text();
          if (body) {
            errorMessage = body;
          }
        } catch {
          // fall back to default message
        }
        throw new ApiError(errorMessage, response.status);
      }

      let data: T;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        data = (await response.text()) as T;
      }

      return {
        data,
        status: response.status,
        statusText: response.statusText,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof ApiError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timeout', BaseApiClient.HTTP_REQUEST_TIMEOUT);
        }
        throw new ApiError(error.message, BaseApiClient.HTTP_UNKNOWN_ERROR);
      }

      throw new ApiError('Unknown error occurred', BaseApiClient.HTTP_UNKNOWN_ERROR);
    }
  }

  /**
   * DELETE request
   */
  protected async delete<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    const options: RequestInit = {
      method: 'DELETE',
    };

    if (body !== undefined) {
      options.body = JSON.stringify(body);
    }

    return this.request<T>(endpoint, options);
  }

  /**
   * PATCH request
   */
  protected async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    const options: RequestInit = {
      method: 'PATCH',
    };

    if (body !== undefined) {
      options.body = JSON.stringify(body);
    }
    return this.request<T>(endpoint, options);
  }

  /**
   * Set authorization header (cached static bearer). Used by standalone mode
   * where the PKCE library hands us a token that's stable until refresh.
   */
  public setAuthToken(token: string): void {
    this.config.headers.Authorization = `Bearer ${token}`;
  }

  /**
   * Remove authorization header
   */
  public clearAuthToken(): void {
    delete this.config.headers.Authorization;
  }

  /**
   * Register a token provider for hosted (MF) mode. When set, each request
   * awaits this function for a fresh bearer rather than reading the cached
   * header. Pass `null` to disable.
   *
   * Registering a provider clears any static Authorization header so
   * setAuthToken()/refreshAuthToken() cannot silently pin a stale bearer
   * ahead of the provider on subsequent requests.
   */
  public setTokenProvider(provider: TokenProvider | null): void {
    this.tokenProvider = provider;
    if (provider) {
      delete this.config.headers.Authorization;
    }
  }

  /**
   * Call the registered `tokenProvider`, but never wait longer than
   * `TOKEN_PROVIDER_TIMEOUT`. If the provider hangs (or rejects), this rejects
   * so the caller can proceed without a fresh token rather than stalling the
   * whole request — and, by extension, any UI gated on its pending state.
   */
  private async getFreshTokenWithTimeout(): Promise<string | null> {
    if (!this.tokenProvider) {
      return null;
    }
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race<string | null>([
        Promise.resolve(this.tokenProvider()),
        new Promise<string | null>((_resolve, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error('tokenProvider timed out')),
            BaseApiClient.TOKEN_PROVIDER_TIMEOUT,
          );
        }),
      ]);
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }
  }

  /**
   * Update configuration
   */
  public updateConfig(newConfig: Partial<ApiConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  public status: number;
  public code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}
